import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import api from '@/services/api'
import type { User, LoginRequest, RegisterRequest } from '@/types'

const TOKEN_KEY = 'ylm_access_token'
const REFRESH_KEY = 'ylm_refresh_token'

export const useAuthStore = defineStore('auth', () => {
  const user = ref<User | null>(null)
  const token = ref(localStorage.getItem(TOKEN_KEY) || '')
  const refreshToken = ref(localStorage.getItem(REFRESH_KEY) || '')

  const isLoggedIn = computed(() => !!token.value)
  const userRole = computed(() => user.value?.role ?? 'student')

  async function login(data: LoginRequest) {
    const res = await api.post('/auth/login', data)
    token.value = res.data.accessToken
    refreshToken.value = res.data.refreshToken
    user.value = res.data.user
    localStorage.setItem(TOKEN_KEY, res.data.accessToken)
    localStorage.setItem(REFRESH_KEY, res.data.refreshToken)
  }

  async function register(data: RegisterRequest) {
    const res = await api.post('/auth/register', data)
    token.value = res.data.accessToken
    refreshToken.value = res.data.refreshToken
    user.value = res.data.user
    localStorage.setItem(TOKEN_KEY, res.data.accessToken)
    localStorage.setItem(REFRESH_KEY, res.data.refreshToken)
  }

  async function fetchUser() {
    const res = await api.get('/auth/me')
    user.value = res.data
  }

  async function refreshTokens() {
    const res = await api.post('/auth/refresh', {
      refreshToken: refreshToken.value,
    })
    token.value = res.data.accessToken
    refreshToken.value = res.data.refreshToken
    localStorage.setItem(TOKEN_KEY, res.data.accessToken)
    localStorage.setItem(REFRESH_KEY, res.data.refreshToken)
  }

  function logout() {
    user.value = null
    token.value = ''
    refreshToken.value = ''
    localStorage.removeItem(TOKEN_KEY)
    localStorage.removeItem(REFRESH_KEY)
  }

  async function updateProfile(data: { nickname?: string; email?: string; phone?: string }) {
    const res = await api.put('/auth/profile', data)
    user.value = res.data
  }

  async function changePassword(currentPassword: string, newPassword: string) {
    await api.put('/auth/password', { currentPassword, newPassword })
  }

  return {
    user,
    token,
    refreshToken,
    isLoggedIn,
    userRole,
    login,
    register,
    fetchUser,
    refreshTokens,
    logout,
    updateProfile,
    changePassword,
  }
})
